import React, { useMemo } from 'react';

import { faChevronRight, faCircle, faComments, faHome, faMinus, faPlus, faRobot, faTimes, faUpload } from '@fortawesome/pro-regular-svg-icons';
// Remove Kendo ButtonGroup imports
// import { Button as KendoButton, ButtonGroup } from '@progress/kendo-react-buttons';
// Kendo DropDownListChangeEvent might be used by core/ui Dropdown, or needs adjustment
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
// Import Kendo Switch and Slider event types
import { Slider } from '@progress/kendo-react-inputs';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { TagModel, WorklistViewModel } from 'models';

import { MOBILE_BREAKPOINT } from 'core/hooks';
// --- Use core/ui components ---
import { Button, ButtonVariants, ComponentSizes, Dropdown, Icon, LinkButton } from 'core/ui';
// Import SliderChangeEvent from core/ui export
import type { SliderChangeEvent as CoreSliderChangeEvent } from 'core/ui';

import type { BreadcrumbItem } from '../services/worklistNavigationService';
import { generateBreadcrumbs, shouldShowBreadcrumb } from '../services/worklistNavigationService';
import { SortCriteria } from './PatientFiles.types';

// Define CaseOption here
type CaseOption = { name: string; value: number | null };

// Define ViewMode type - UPDATED to include 'data'
type ViewMode = 'gallery' | 'stacked' | 'data';

// -----------------------------------
// Constants
// -----------------------------------
// const SORT_OPTIONS: { name: string; value: SortCriteria }[] = [
//   { name: 'Newest First', value: 'newest' },
//   { name: 'Oldest First', value: 'oldest' },
//   { name: 'File Name (A-Z)', value: 'name' },
// ];

// Using MOBILE_BREAKPOINT constant from core/hooks for consistency

// -----------------------------------
// Styled components
// -----------------------------------
const HeaderContainer = styled.div`
  /* Follow CSS Layout Guide: Flex layout with proper spacing */
  display: flex;
  align-items: center;
  justify-content: space-between; /* Distribute space between left and right sections */
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderBase};
  gap: ${({ theme }) => theme.space.spacing20};

  /* Mobile responsive adjustments following CSS Layout Guide */
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.space.spacing10};
  }
`;

const LeftSection = styled.div`
  /* Follow CSS Layout Guide: Fill remaining space */
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.spacing20};
  flex: 1 1 0; /* Grow, shrink, zero basis */
  min-width: 0; /* Allow shrinking below content size */
`;

const RightSection = styled.div`
  /* Follow CSS Layout Guide: Fixed size, no grow/shrink */
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.spacing20};
  flex: 0 0 auto; /* Don't grow, don't shrink, size to content */

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    order: -1; /* Move to top on mobile */
    width: 100%;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.space.spacing10};
  }
`;

const BreadcrumbContainer = styled.div`
  /* Follow CSS Layout Guide: Fill available space */
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.space.spacing10};
  flex: 1 1 0; /* Grow, shrink, zero basis */
  min-width: 0; /* Allow shrinking below content size */

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none; /* Hide breadcrumbs on mobile to save space */
  }
`;

const BreadcrumbItem = styled.button<{ isActive?: boolean }>`
  background: none;
  border: none;
  color: ${({ isActive }) => (isActive ? '#333' : '#007acc')};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  cursor: ${({ isActive }) => (isActive ? 'default' : 'pointer')};
  font-size: ${({ theme }) => theme.fontSizes.body}; /* Increased from 0.875rem */
  font-weight: ${({ theme }) => theme.fontWeights.normal};
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:hover:not(:disabled) {
    background-color: #f5f5f5;
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
`;

const BreadcrumbSeparator = styled.span`
  color: #666;
  font-size: ${({ theme }) => theme.fontSizes.body};
`;

// Style for the core/ui Dropdown with improved sizing
const StyledDropdown = styled(Dropdown)`
  flex: 0 0 140px; /* Increased width for better readability */
  min-width: 140px;

  & > div {
    font-size: ${({ theme }) => theme.fontSizes.body}; /* Larger font */
  }

  .k-dropdown-wrap {
    border-radius: ${({ theme }) => theme.radii.base};
    border: 1px solid ${({ theme }) => theme.colors.palette.grayscale[4]};

    &:hover {
      border-color: ${({ theme }) => theme.colors.palette.grayscale[5]};
    }
  }
`;

// Style the wrapper for the slider + label
const SliderWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  max-width: 200px;

  .column-slider {
    flex-grow: 1;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none; /* Hide on mobile to save space */
  }
`;

const StyledBreadcrumbSeparator = styled.span`
  margin: 0 8px;
  color: #6c757d;
  font-size: ${({ theme }) => theme.fontSizes.body};
`;

// Add the missing StyledUpdateIndicator styled component
const StyledUpdateIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background-color: ${({ theme }) => theme.colors.palette?.greens?.[1] || '#d4edda'};
  border: 1px solid ${({ theme }) => theme.colors.palette?.greens?.[3] || '#c3e6cb'};
  border-radius: 4px;
  font-size: ${({ theme }) => theme.fontSizes.body};
  color: ${({ theme }) => theme.colors.palette?.greens?.[8] || '#155724'};
  animation: fadeIn 0.3s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  span:first-of-type {
    font-weight: 600;
  }
`;

// Enhanced upload button styling
const StyledUploadButton = styled(Button)`
  font-size: ${({ theme }) => theme.fontSizes.body};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  padding: ${({ theme }) => theme.space.spacing20} ${({ theme }) => theme.space.spacing40};

  /* Ensure proper spacing for icon and text */
  .icon-container {
    margin-right: 8px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex: 1;
    padding: ${({ theme }) => theme.space.spacing10} ${({ theme }) => theme.space.spacing20};
    font-size: ${({ theme }) => theme.fontSizes.footnote};
  }
`;

// Template manager button styling
const StyledTemplateButton = styled(Button)`
  font-size: ${({ theme }) => theme.fontSizes.body};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  padding: ${({ theme }) => theme.space.spacing20} ${({ theme }) => theme.space.spacing40};
  margin-right: ${({ theme }) => theme.space.spacing10};

  /* Ensure proper spacing for icon and text */
  .icon-container {
    margin-right: 8px;
  }
`;

// Chat button styling
const StyledChatButton = styled(Button)`
  font-size: ${({ theme }) => theme.fontSizes.body};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  padding: ${({ theme }) => theme.space.spacing20} ${({ theme }) => theme.space.spacing40};
  margin-right: ${({ theme }) => theme.space.spacing10};

  /* Ensure proper spacing for icon and text */
  .icon-container {
    margin-right: 8px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex: 1;
    margin-right: 0;
    padding: ${({ theme }) => theme.space.spacing10} ${({ theme }) => theme.space.spacing20};
    font-size: ${({ theme }) => theme.fontSizes.footnote};
  }
`;

// Categories button styling to match other buttons
const StyledCategoriesButton = styled(Button)`
  font-size: ${({ theme }) => theme.fontSizes.body};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  padding: ${({ theme }) => theme.space.spacing20} ${({ theme }) => theme.space.spacing20};
  margin-right: ${({ theme }) => theme.space.spacing10};

  /* Ensure proper spacing for icon and text */
  .icon-container {
    margin-right: 8px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex: 1;
    margin-right: 0;
    padding: ${({ theme }) => theme.space.spacing10} ${({ theme }) => theme.space.spacing20};
    font-size: ${({ theme }) => theme.fontSizes.footnote};
  }
`;

// -----------------------------------
// Props Interface - Use Kendo event type for Dropdown
// -----------------------------------
interface PatientFilesHeaderProps {
  sortCriteria: SortCriteria;
  onSortCriteriaChange: (e: DropDownListChangeEvent) => void;
  numberOfColumns: number;
  onNumberOfColumnsChange: (newCount: number) => void;
  onToggleSidebar: () => void;
  caseOptions: CaseOption[];
  selectedCaseId: number | null;
  onCaseChange: (e: DropDownListChangeEvent) => void;
  isLoading: boolean;
  // viewMode?: ViewMode; // Removed - always use smart stacking
  // onViewModeChange?: (mode: ViewMode) => void; // Removed - always use smart stacking
  isMobile: boolean;
  onUploadClick: () => void;
  onChatClick?: () => void;
  isChatOpen?: boolean;
  sidebarExpanded?: boolean;
  selectedWorklistName?: string;
  onTemplateManagerClick?: () => void;
  isTemplateManagerOpen?: boolean;
  // Breadcrumb props
  patientId: number;
  hierarchicalPath?: string;
  mergedFlatTags?: TagModel[] | null;
  mergedTagNameToIdMap?: Map<string, number>;
  selectedWorklist?: WorklistViewModel | null;
  allWorklists?: WorklistViewModel[] | null;
  stackName?: string;
  shareContext?: {
    shareLinkId: string;
    isShareMode: true;
  };
  // Slot for toolbar prefix (collapsed worklist view)
  slotToolbarPrefix?: React.ReactNode;
  // Data update indicator props
  hasDataUpdates?: boolean;
  lastUpdateTime?: Date | null;
  // Upload mode indicator
  isUploadMode?: boolean;
}

// -----------------------------------
// Component
// -----------------------------------
const slugify = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

export const PatientFilesHeader: React.FC<PatientFilesHeaderProps> = ({
  sortCriteria,
  onSortCriteriaChange,
  numberOfColumns,
  onNumberOfColumnsChange,
  onToggleSidebar,
  caseOptions,
  selectedCaseId,
  onCaseChange,
  isLoading,
  // viewMode = 'gallery', // Removed - always use smart stacking
  // onViewModeChange, // Removed - always use smart stacking
  isMobile,
  onUploadClick,
  onChatClick,
  isChatOpen = false,
  sidebarExpanded = false,
  selectedWorklistName,
  onTemplateManagerClick,
  isTemplateManagerOpen = false,
  // Breadcrumb props
  patientId,
  hierarchicalPath,
  mergedFlatTags,
  mergedTagNameToIdMap,
  selectedWorklist,
  allWorklists,
  stackName,
  shareContext,
  // Slot for toolbar prefix (collapsed worklist view)
  slotToolbarPrefix,
  // Data update indicator props
  hasDataUpdates = false,
  lastUpdateTime = null,
  // Upload mode indicator
  isUploadMode = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const sortOptions = ['Newest', 'Oldest', 'Name'];

  // Breadcrumb logic using unified navigation service
  const breadcrumbs = useMemo(() => {
    return generateBreadcrumbs({
      patientId,
      currentPathname: location.pathname,
      selectedWorklist,
      allWorklists,
      stackName,
      hierarchicalTags: null,
      shareContext,
    });
  }, [patientId, selectedWorklist, allWorklists, stackName, location.pathname, shareContext]);

  const handleBreadcrumbClick = (breadcrumb: BreadcrumbItem) => {
    // Don't navigate for virtual items or if already on the same path
    if (breadcrumb.isVirtual || breadcrumb.path === location.pathname) {
      return;
    }
    navigate(breadcrumb.path);
  };

  const isCurrentPath = (breadcrumbPath: string) => {
    return location.pathname === breadcrumbPath;
  };

  // Handler for Kendo Slider component
  const handleColumnChange = (event: CoreSliderChangeEvent) => {
    // event.value is the VISUAL value from the slider (1=small size, 8=large size)
    // Convert it back to the actual column count (1=large size, 8=small size)
    const minCols = 1;
    const maxCols = 8;
    const actualValue = Math.round(maxCols + minCols - event.value);

    // Clamp the value just in case
    const clampedActualValue = Math.max(minCols, Math.min(maxCols, actualValue));

    if (clampedActualValue !== numberOfColumns) {
      onNumberOfColumnsChange(clampedActualValue);
    }
  };

  const handleDecrement = () => {
    const newValue = Math.max(1, numberOfColumns - 1);
    if (newValue !== numberOfColumns) {
      onNumberOfColumnsChange(newValue);
    }
  };

  const handleIncrement = () => {
    const newValue = Math.min(8, numberOfColumns + 1);
    if (newValue !== numberOfColumns) {
      onNumberOfColumnsChange(newValue);
    }
  };

  // Removed toggle button handler - always use smart stacking
  // const handleToggleViewClick = () => {
  //   // If we have a data view, this toggle logic might need to be more sophisticated
  //   // or we might need separate buttons for each view mode.
  //   // For now, this only toggles gallery/stacked. The 'data' view is set by other means in PatientFiles.tsx.
  //   if (viewMode === 'gallery') {
  //     onViewModeChange?.('stacked');
  //   } else if (viewMode === 'stacked') {
  //     onViewModeChange?.('gallery');
  //   }
  //   // If viewMode is 'data', this button currently does nothing to change it.
  // };

  // Calculate the visual value for the slider (inverse of column count)
  const minCols = 1;
  const maxCols = 8;
  const visualSliderValue = maxCols + minCols - numberOfColumns;

  // Check if there's any content to show
  const hasLeftContent = (slotToolbarPrefix && !isMobile) || hasDataUpdates || shouldShowBreadcrumb(breadcrumbs);
  const hasRightContent = !isMobile && !isUploadMode; // Column slider
  const hasUploadButton = !shareContext?.isShareMode || !isUploadMode; // Hide upload button in share mode when in upload mode (no cancel button)
  const hasAnyContent = hasLeftContent || hasRightContent || hasUploadButton || (isMobile && (onToggleSidebar || onChatClick));

  // Don't render header if there's no content (especially on mobile in share mode)
  if (!hasAnyContent) {
    return null;
  }

  return (
    <HeaderContainer>
      <LeftSection>
        {slotToolbarPrefix && !isMobile && <div>{slotToolbarPrefix}</div>}

        {/* Data Update Indicator */}
        {hasDataUpdates && (
          <StyledUpdateIndicator>
            <Icon icon={faCircle} style={{ color: '#28a745', fontSize: '8px' }} />
            <span>Data updated</span>
            {lastUpdateTime && <span style={{ fontSize: '0.8em', opacity: 0.7 }}>{lastUpdateTime.toLocaleTimeString()}</span>}
          </StyledUpdateIndicator>
        )}

        {/* Breadcrumb - only show if there are multiple items */}
        {shouldShowBreadcrumb(breadcrumbs) && (
          <BreadcrumbContainer>
            {breadcrumbs.map((breadcrumb, index) => (
              <React.Fragment key={breadcrumb.id || `item-${index}`}>
                {index > 0 && (
                  <BreadcrumbSeparator>
                    <Icon icon={faChevronRight} />
                  </BreadcrumbSeparator>
                )}
                <BreadcrumbItem
                  isActive={isCurrentPath(breadcrumb.path) || breadcrumb.isVirtual}
                  disabled={isCurrentPath(breadcrumb.path) || breadcrumb.isVirtual}
                  onClick={() => handleBreadcrumbClick(breadcrumb)}
                >
                  {breadcrumb.isRoot && <Icon icon={faHome} style={{ marginRight: '0.25rem' }} />}
                  {breadcrumb.name}
                </BreadcrumbItem>
              </React.Fragment>
            ))}
          </BreadcrumbContainer>
        )}
      </LeftSection>

      <RightSection>
        {/* Sort Dropdown */}
        {!isMobile && !isUploadMode && (
          <StyledDropdown
            data={sortOptions.map((option) => ({ name: option, value: option }))}
            value={sortCriteria}
            onChange={onSortCriteriaChange}
            disabled={isLoading}
            textField="name"
            dataItemKey="value"
            valueField="value"
            isForPrimitiveValues={true}
          />
        )}

        {/* Column Count Slider - Hide on mobile and in upload mode */}
        {!isMobile && !isUploadMode && (
          <SliderWrapper title={`Columns: ${numberOfColumns}`}>
            <LinkButton
              onClick={handleDecrement}
              disabled={isLoading || numberOfColumns <= 1}
              size={ComponentSizes.SMALL}
              title="Fewer columns (larger thumbnails)"
            >
              <Icon icon={faMinus} />
            </LinkButton>
            <Slider
              min={minCols}
              max={maxCols}
              step={1}
              value={visualSliderValue}
              onChange={handleColumnChange}
              disabled={isLoading}
              aria-label="Number of Columns"
              className="column-slider"
            />
            <LinkButton
              onClick={handleIncrement}
              disabled={isLoading || numberOfColumns >= 8}
              size={ComponentSizes.SMALL}
              title="More columns (smaller thumbnails)"
            >
              <Icon icon={faPlus} />
            </LinkButton>
          </SliderWrapper>
        )}

        {/* AI Template Manager Button - Only show if handler is provided and worklist is selected */}
        {onTemplateManagerClick && !isMobile && !shareContext?.isShareMode && selectedWorklist && selectedWorklist.id !== -1 && (
          <StyledTemplateButton
            variant={isTemplateManagerOpen ? ButtonVariants.PRIMARY : ButtonVariants.SECONDARY}
            onClick={onTemplateManagerClick}
            disabled={isLoading}
            title={isTemplateManagerOpen ? 'Close AI Templates' : 'Manage AI Templates'}
          >
            <Icon icon={faRobot} />
            AI Templates
          </StyledTemplateButton>
        )}

        {/* Upload Button - Hide in share mode when in upload mode (no cancel functionality) */}
        {hasUploadButton && (
          <StyledUploadButton variant={isUploadMode ? ButtonVariants.SECONDARY : ButtonVariants.PRIMARY} onClick={onUploadClick} disabled={isLoading}>
            <Icon icon={isUploadMode ? faTimes : faUpload} />
            {isUploadMode ? (isMobile ? 'Cancel' : 'Cancel Upload') : isMobile ? 'Upload' : 'Upload Files'}
          </StyledUploadButton>
        )}

        {/* AI Chat Button - Show on both desktop and mobile */}
        {onChatClick && !shareContext?.isShareMode && (
          <StyledChatButton
            variant={isChatOpen ? ButtonVariants.PRIMARY : ButtonVariants.SECONDARY}
            onClick={onChatClick}
            disabled={isLoading}
            title={isChatOpen ? 'Close AI Chat' : 'Open AI Chat'}
          >
            <Icon icon={faComments} />
            {isMobile ? 'Chat' : 'AI Chat'}
          </StyledChatButton>
        )}
      </RightSection>
    </HeaderContainer>
  );
};
